import React, { useState } from "react";
import { Button, Input, Space, Typography, Modal } from "antd";
import { KeyOutlined, MailOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { getMsg } from "../utils/antdStatic";
import { generatePassword } from "../utils/generatePassword";
import { usersApi } from "../services/users.api";

interface AdminPasswordToolsProps {
  /** Form de AntD que contiene el campo de contraseña. */
  form: FormInstance;
  /** Nombre del campo de contraseña en el form (default "password"). */
  passwordField?: string;
  /**
   * Id del usuario para "Enviar recuperación". Si es undefined (p.ej. en
   * CREACIÓN, cuando el usuario aún no existe) ese botón no se muestra.
   */
  userId?: number;
  /** Email actual del usuario, pre-rellena el destino de la recuperación. */
  currentEmail?: string;
  /**
   * Mostrar el botón "Generar contraseña". Default true. Se pone en false
   * cuando el modal ya tiene su propio generador (p. ej. el de clientes), para
   * no duplicarlo y dejar solo "Enviar recuperación".
   */
  showGenerate?: boolean;
}

/**
 * Herramientas de contraseña para el admin, pensadas para vivir dentro de los
 * modales de edición/creación de aseador y cliente:
 *
 *  - "Generar contraseña": rellena el campo del form con una clave aleatoria
 *    fuerte y la muestra (copiable) para que el admin la comparta. Al guardar
 *    el form, esa clave se persiste por el flujo normal (updateCleaner /
 *    upsert).
 *  - "Enviar correo de recuperación": dispara el correo de reset para que el
 *    propio usuario fije su contraseña. Permite enviarlo al email registrado
 *    o a otro (sin cambiar el email de la cuenta).
 *
 * Round 2026-06-23.
 */
const AdminPasswordTools: React.FC<AdminPasswordToolsProps> = ({
  form,
  passwordField = "password",
  userId,
  currentEmail,
  showGenerate = true,
}) => {
  const [generated, setGenerated] = useState<string | null>(null);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [sending, setSending] = useState(false);

  const handleGenerate = () => {
    const pwd = generatePassword();
    form.setFieldValue(passwordField, pwd);
    setGenerated(pwd);
    getMsg()?.success(
      "Contraseña generada y rellenada. Cópiala antes de guardar — no se mostrará después.",
    );
  };

  const openRecovery = () => {
    setRecoveryEmail(currentEmail ?? "");
    setRecoveryOpen(true);
  };

  const handleSendRecovery = async () => {
    if (userId == null) return;
    const dest = recoveryEmail.trim();
    setSending(true);
    // Solo mandamos el override si difiere del email actual; si está vacío o
    // es el mismo, el backend usa el email registrado.
    const override =
      dest && dest.toLowerCase() !== (currentEmail ?? "").toLowerCase()
        ? dest
        : undefined;
    const res = await usersApi.sendRecovery(userId, override);
    setSending(false);
    if (res) {
      getMsg()?.success(
        (res as { message?: string })?.message ||
          `Correo de recuperación enviado a ${dest || currentEmail}.`,
      );
      setRecoveryOpen(false);
    }
  };

  return (
    <div style={{ marginTop: -4, marginBottom: 12 }}>
      <Space wrap size={8}>
        {showGenerate && (
          <Button icon={<KeyOutlined />} onClick={handleGenerate}>
            Generar contraseña
          </Button>
        )}
        {userId != null && (
          <Button icon={<MailOutlined />} onClick={openRecovery}>
            Enviar correo de recuperación
          </Button>
        )}
      </Space>

      {showGenerate && generated && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ marginRight: 6 }}>
            Generada:
          </Typography.Text>
          <Typography.Text copyable code>
            {generated}
          </Typography.Text>
        </div>
      )}

      {/* Modal de destino del correo de recuperación. Se monta solo cuando el
          admin lo abre; lo renderizamos como componente controlado (no
          Modal.confirm) para poder embeber el input editable. */}
      <Modal
        title="Enviar correo de recuperación"
        open={recoveryOpen}
        onOk={handleSendRecovery}
        onCancel={() => setRecoveryOpen(false)}
        okText="Enviar"
        cancelText="Cancelar"
        confirmLoading={sending}
        // Evita que el form padre intercepte el Enter del input.
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Se enviará un enlace para que el usuario fije su propia contraseña
          (válido 15 minutos). Por defecto va a su correo registrado; puedes
          cambiar el destino sin alterar el email de la cuenta.
        </Typography.Paragraph>
        <Input
          type="email"
          value={recoveryEmail}
          onChange={(e) => setRecoveryEmail(e.target.value)}
          placeholder="correo@destino.com"
          onPressEnter={handleSendRecovery}
        />
      </Modal>
    </div>
  );
};

export default AdminPasswordTools;
